import { useCallback, useEffect, useMemo, useState } from 'react';
import { matterApi, taskApi, type StagePlaybookEntry, type Task } from '../../api';

type Props = { matterId: number | string; onChanged?: () => void };

export default function StagePlaybook({ matterId, onChanged }: Props) {
  const [stages, setStages] = useState<StagePlaybookEntry[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [advancing, setAdvancing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, t] = await Promise.all([
        matterApi.stagePlaybook(matterId),
        taskApi.list({ matter_id: matterId }).catch(() => ({ tasks: [] as Task[] })),
      ]);
      setStages(r.stages || []);
      setTasks(t.tasks || []);
    } catch { /* not all matter types have stages */ }
    finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  // Auto-advance prompt: if every advocate-owned task on the current stage is
  // completed, surface a banner offering to advance. Attorney-owned tasks
  // don't block advance — the advocate can't complete them.
  const currentStage = stages.find((s) => s.status === 'current') || null;
  const currentStageTasks = useMemo(() => {
    if (!currentStage) return [] as Task[];
    return tasks.filter((t) =>
      String((t as Task & { stage_id?: string | number | null }).stage_id || '') === currentStage.id
      && (t as Task & { owner_role?: string | null }).owner_role !== 'attorney'
    );
  }, [tasks, currentStage]);
  const allCurrentDone = currentStageTasks.length > 0 &&
    currentStageTasks.every((t) => t.status === 'completed' || t.status === 'cancelled');

  const advance = async () => {
    setAdvancing(true);
    try {
      await matterApi.advanceStage(matterId);
      await load();
      onChanged?.();
    } catch (e) {
      alert((e as Error).message || 'Could not advance stage.');
    } finally {
      setAdvancing(false);
    }
  };

  const skip = async (s: StagePlaybookEntry) => {
    const reason = window.prompt(
      `Skip "${s.name}"?\n\nThe stage stays in the playbook (greyed out) for audit. Reason (optional):`,
      '',
    );
    if (reason === null) return; // user cancelled
    setBusy(s.id);
    try {
      await matterApi.skipStage(matterId, s.id, reason || undefined);
      await load();
      onChanged?.();
    } catch (e) {
      alert((e as Error).message || 'Could not skip stage.');
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="muted" style={{ padding: 8, fontSize: 13 }}>Loading roadmap…</div>;
  if (!stages.length) {
    return <div className="muted" style={{ padding: 8, fontSize: 13 }}>No procedural stages defined for this matter type.</div>;
  }

  return (
    <>
      {allCurrentDone && currentStage && (
        <div style={{
          padding: '10px 14px', marginBottom: 14,
          background: 'rgba(46,125,50,0.10)', borderLeft: '4px solid var(--color-success)',
          borderRadius: 4, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ flex: 1 }}>
            <b>All advocate tasks for "{currentStage.name}" are complete.</b>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
              Ready to advance the matter to the next procedural stage?
            </div>
          </div>
          <button className="primary" onClick={advance} disabled={advancing}>
            {advancing ? 'Advancing…' : 'Advance stage →'}
          </button>
        </div>
      )}
    <ol style={{ listStyle: 'none', padding: 0, margin: 0 }}>
      {stages.map((s, idx) => {
        // past_unmarked has been collapsed into past (per advocate feedback —
        // if the user has moved past a stage they consider it done; the
        // play-by-play tasks may still be open and visible separately).
        const isPast    = s.status === 'past' || s.status === 'past_unmarked';
        const isActive  = s.status === 'current';
        const isSkipped = s.status === 'skipped';
        const color = isActive ? 'var(--color-accent)'
                    : isPast   ? 'var(--color-success)'
                    : isSkipped ? 'var(--color-text-muted)'
                               : 'var(--color-border-strong)';
        const opacity = isSkipped ? 0.5 : 1;
        // Optional stage marker for upcoming stages: dashed border instead of solid.
        const isUpcomingOptional = s.is_optional && s.status === 'upcoming';
        const notes = (s.notes_csv || '')
          .split('|')
          .map((n) => n.trim())
          .filter(Boolean);
        return (
          <li key={s.id} style={{ display: 'flex', gap: 14, paddingBottom: idx === stages.length - 1 ? 0 : 16, opacity }}>
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: isActive ? 'var(--color-accent)' : 'var(--color-surface)',
                border: `2px ${isUpcomingOptional ? 'dashed' : 'solid'} ${color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: isActive ? '#1A1305' : color,
                fontSize: 11, fontWeight: 700,
                flexShrink: 0,
                textDecoration: isSkipped ? 'line-through' : 'none',
              }}>
                {/* Sequence numbers are deliberately NOT rendered. Glyphs:
                    ✓ past · ● current · ○ upcoming · − skipped. */}
                {isSkipped ? '−'
                  : isPast ? '✓'
                  : isActive ? '●'
                  : '○'}
              </div>
              {idx < stages.length - 1 && (
                <div style={{
                  width: 2, flex: 1, background: isPast ? 'var(--color-success)' : 'var(--color-border)',
                  marginTop: 4,
                }} />
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0, paddingBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <div style={{
                  fontWeight: 600,
                  color: isActive ? 'var(--color-accent)' : 'var(--color-text)',
                  textDecoration: isSkipped ? 'line-through' : 'none',
                }}>
                  {s.name}
                </div>
                {isActive && <span className="pill accent" style={{ fontSize: 10 }}>current</span>}
                {isSkipped && <span className="pill" style={{ fontSize: 10 }}>skipped</span>}
                {s.is_optional && !isSkipped && !isPast && <span className="pill" style={{ fontSize: 10 }}>optional</span>}
                {s.default_duration_days != null && (
                  <span className="muted" style={{ fontSize: 12 }}>~{s.default_duration_days} days</span>
                )}
                {/* Skip is only offered for the *current* stage if it's optional —
                    skipping a future stage doesn't make sense (advance through it
                    first), and a past stage is already done. */}
                {isActive && s.is_optional && (
                  <button
                    className="ghost"
                    style={{ marginLeft: 'auto', padding: '2px 10px', fontSize: 12 }}
                    onClick={() => skip(s)}
                    disabled={busy === s.id}
                  >
                    {busy === s.id ? '…' : 'Skip'}
                  </button>
                )}
              </div>
              {s.description && (
                <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{s.description}</div>
              )}
              {notes.length > 0 && (
                <div style={{
                  marginTop: 6, padding: '6px 10px',
                  background: 'rgba(201,163,74,0.10)',
                  borderLeft: '3px solid var(--color-accent)',
                  borderRadius: 4,
                  fontSize: 12,
                }}>
                  {notes.map((n, i) => <div key={i}>{n}</div>)}
                </div>
              )}
              {(s.tasks.length > 0 || s.deadlines.length > 0) && (
                <div style={{
                  marginTop: 8, padding: '8px 10px',
                  background: 'var(--color-surface-2)', borderRadius: 6,
                  fontSize: 12,
                }}>
                  {s.tasks.length > 0 && (
                    <div>
                      <span className="muted" style={{ fontWeight: 600, marginRight: 6 }}>Tasks:</span>
                      {s.tasks.map((t, i) => (
                        <span key={i}>
                          {t.name}
                          {t.offset_days != null ? ` (+${t.offset_days}d)` : ''}
                          {i < s.tasks.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                  {s.deadlines.length > 0 && (
                    <div style={{ marginTop: s.tasks.length ? 4 : 0 }}>
                      <span className="muted" style={{ fontWeight: 600, marginRight: 6 }}>Deadlines:</span>
                      {s.deadlines.map((d, i) => (
                        <span key={i}>
                          {d.name} (+{d.offset_days} {d.counts_court_days ? 'court' : 'cal'}d{d.severity === 'statutory' ? ' · statutory' : ''})
                          {i < s.deadlines.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
    </>
  );
}
